import { useEffect, useState } from 'react';
import { SearchingIcon } from '@icons';

import { inputStyles } from '@components/input/input.config';

import Icon from '../icon/Icon';

interface SearchBarProps {
  inputId?: string;
  placeholder: string;
  SearchIcon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onSearch: (searchQuery: string) => void;
  searchBarStyle?: string;
  iconStyle?: string;
  searchInputStyle?: string;
  value?: string;
  clearSearchInput?: any; // prop to clear the local searchText state
  stopEventPropagation?: boolean;
  isSearchDigitsOnly?: boolean;
}

const SearchBar = ({
  inputId,
  placeholder,
  SearchIcon = SearchingIcon,
  onSearch,
  searchBarStyle,
  iconStyle,
  searchInputStyle,
  value,
  clearSearchInput,
  stopEventPropagation = false,
  isSearchDigitsOnly = false
}: SearchBarProps) => {
  const [searchText, setSearchText] = useState<string>(value || '');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isSearchDigitsOnly && event.target.value) {
      const isDigitsOnly = /^[0-9]+$/.test(event.target.value);
      if (!isDigitsOnly) {
        return;
      }
    }
    setSearchText(event.target.value);
    event.stopPropagation();
  };

  const handleSearchBarClear = () => {
    setSearchText('');
    onSearch('');
  };

  const onInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (stopEventPropagation) {
      event.stopPropagation();
    }
  };

  useEffect(() => {
    setSearchText(value || '');
  }, [value]);

  useEffect(() => {
    if (clearSearchInput) {
      handleSearchBarClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearSearchInput]);

  useEffect(() => {
    const debounceFn = setTimeout(() => {
      onSearch(searchText);
    }, 1000);

    return () => {
      if (debounceFn) clearTimeout(debounceFn);
    };
  }, [searchText, onSearch]);

  return (
    <div className={`relative h-11 ${searchBarStyle || ''}`}>
      {SearchIcon && (
        <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
          <SearchIcon className={iconStyle || ''} />
        </div>
      )}
      <input
        data-testid={inputId}
        value={searchText}
        placeholder={placeholder}
        onChange={handleInputChange}
        onClick={(e) => onInputClick(e)}
        type="text"
        className={`placeholder:text-text-grey size-full border-grey30 bg-white py-2 pl-10 pr-9 text-sm ${
          inputStyles.common
        } ${searchInputStyle || ''}`}
      />
      {searchText && (
        <div
          role="presentation"
          onClick={handleSearchBarClear}
          className="absolute inset-y-0 right-4 flex cursor-pointer items-center"
        >
          <Icon name="close" />
        </div>
      )}
    </div>
  );
};

export default SearchBar;
